/**
 * The chrome every location section wears: the "Show all N" pill and the
 * collapsible card header.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011). `ShowMoreFooter`
 * is a good component in the wrong place -- three other surfaces
 * re-implement it -- but promoting it to `components/ui/` is #951 PR 6's
 * job, not this move's. Moving it and promoting it in one commit would
 * make both unreviewable.
 */
import { useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
// Promoted to components/ui in #1011 — three surfaces re-implemented it and
// the copies had drifted. Re-exported here so the location sections' imports
// stay put.
export { ShowMoreFooter } from "@/components/ui/show-more-footer";

/**
 * A whole trade section (Tests / Jobs / Attachments) that collapses to a single
 * header row. The count — and any urgent badge (e.g. overdue) — stays visible
 * while collapsed, so the page opens short and scannable and the secondary
 * tables are one click away instead of a screen of scrolling below Devices.
 */
export function CollapsibleSection({
    title,
    icon,
    count,
    defaultOpen = false,
    badge,
    action,
    children,
}: {
    title: string;
    icon: ReactNode;
    count?: number;
    defaultOpen?: boolean;
    badge?: ReactNode;
    action?: ReactNode;
    children: ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card>
            <CardHeader className="p-0">
                <div className="flex flex-row items-center justify-between gap-2 p-5 pb-3">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                        <ChevronRight
                            className={cn(
                                "h-4 w-4 shrink-0 text-foreground/50 transition-transform",
                                open && "rotate-90",
                            )}
                        />
                        {icon}
                        <span className="text-base font-semibold tracking-tight">{title}</span>
                        {typeof count === "number" && (
                            <span className="text-sm font-normal text-foreground/60">({count})</span>
                        )}
                        {badge}
                    </button>
                    {action && <div className="shrink-0">{action}</div>}
                </div>
            </CardHeader>
            {open && <CardContent>{children}</CardContent>}
        </Card>
    );
}
